import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContentMap } from "@/mocks/article-content";
import { articleCards } from "@/mocks/seo-data";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.blog";

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "");
  const article = articleContentMap[slug];
  const card = articleCards.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | USFansSpreadsheet`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Artykuł nie znaleziony</h1>
            <Link to="/" className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
              ← Powrót do strony głównej
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${SITE_URL}/${slug}`;
  const isFaqArticle = slug === "artykul/usfansspreadsheet-faq-2026";

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* Article Header */}
        <section className="bg-background-50 py-16 md:py-24 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-8 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              ← Powrót do strony głównej
            </Link>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.lastUpdated}
              </span>
              <span className="bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-medium">
                SEO
              </span>
            </div>

            {/* Feature image */}
            {card?.image && (
              <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/7]">
                <img
                  src={card.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            )}
          </div>
        </section>

        {/* Article Content */}
        <article className="bg-background-50 pb-16 md:pb-24 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Intro */}
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-12">
              {article.intro}
            </p>

            {/* Sections */}
            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  {section.title}
                </h2>
                {section.content && (
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                )}
                {section.items && section.items.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {section.items.map((item, i) => (
                      <div key={i} className="p-5 bg-background-100 rounded-xl">
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">
                          <i className="ri-check-line text-primary-500 mr-2"></i>
                          {item.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* CTA */}
            <div className="bg-primary-50 rounded-2xl p-8 text-center border border-primary-200">
              <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">
                {article.ctaTitle}
              </h3>
              <p className="text-sm text-foreground-600 mb-6">{article.ctaDesc}</p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-foreground-950 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                {article.ctaButton}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Back to home */}
            <div className="mt-12 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                ← Powrót do strony głównej
              </Link>
            </div>
          </div>
        </article>
      </main>

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.subtitle,
            url: articleUrl,
            author: {
              "@type": "Person",
              name: article.author,
            },
            datePublished: "2026-06-24",
            dateModified: "2026-06-24",
            publisher: {
              "@type": "Organization",
              name: "USFansSpreadsheet",
              url: SITE_URL,
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": articleUrl,
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "USFansSpreadsheet",
                item: SITE_URL,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.title,
                item: articleUrl,
              },
            ],
          }),
        }}
      />

      {/* FAQPage Schema for FAQ article */}
      {isFaqArticle && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: article.sections.map((section) => ({
                "@type": "Question",
                name: section.title,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: section.content || (section.items ? section.items.map((it) => `${it.title}: ${it.desc}`).join(" ") : ""),
                },
              })),
            }),
          }}
        />
      )}

      <Footer />
    </>
  );
}